/* ──────────────────────────────────────────────────────────────────────
 *  App Root — Team C owns this file
 *  AuthProvider → Router
 * ────────────────────────────────────────────────────────────────────── */

import { createBrowserRouter, RouterProvider, Outlet, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { isMockMode } from "@/lib/env";
import { SetupLayout } from "@/components/SetupLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// ─── Lazy Route Imports ──────────────────────────────────────────────
import { lazy, Suspense, type ReactNode } from "react";

const SignupPage = lazy(() => import("@/routes/SignupPage"));
const ChooseAssistantPage = lazy(() => import("@/routes/ChooseAssistantPage"));
const ConnectionsPage = lazy(() => import("@/routes/ConnectionsPage"));
const DefinePage = lazy(() => import("@/routes/DefinePage"));
const ReviewPage = lazy(() => import("@/routes/ReviewPage"));
const CheckoutReturnPage = lazy(() => import("@/routes/CheckoutReturnPage"));
const ActivatePage = lazy(() => import("@/routes/ActivatePage"));
const AssistantsPage = lazy(() => import("@/routes/AssistantsPage"));
const AssistantDetailPage = lazy(() => import("@/routes/AssistantDetailPage"));
const BillingPage = lazy(() => import("@/routes/BillingPage"));
const SupportPage = lazy(() => import("@/routes/SupportPage"));

// ─── Loading Fallback ────────────────────────────────────────────────

function PageLoader() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
      <div className="spinner" />
    </div>
  );
}

function SuspenseWrap({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ─── Protected Route ─────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, signIn } = useAuth();

  if (loading) return <PageLoader />;

  if (!user) {
    // In mock mode, user should always be present
    // In real mode, redirect to auth
    signIn();
    return <PageLoader />;
  }

  return <>{children}</>;
}

// ─── Router ──────────────────────────────────────────────────────────

const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/signup" replace />,
  },
  {
    path: "/signup",
    element: (
      <SuspenseWrap>
        <SignupPage />
      </SuspenseWrap>
    ),
  },
  {
    // Setup flow — protected + stepper layout
    element: (
      <ProtectedRoute>
        <SetupLayout>
          <Outlet />
        </SetupLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/choose-assistant",
        element: (
          <SuspenseWrap>
            <ChooseAssistantPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/connections",
        element: (
          <SuspenseWrap>
            <ConnectionsPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/define",
        element: (
          <SuspenseWrap>
            <DefinePage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/review",
        element: (
          <SuspenseWrap>
            <ReviewPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/checkout/return",
        element: (
          <SuspenseWrap>
            <CheckoutReturnPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/connect",
        element: (
          <SuspenseWrap>
            <ActivatePage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/activate",
        element: <Navigate to="/connect" replace />,
      },
    ],
  },
  {
    // Dashboard routes — protected, no stepper
    element: (
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/assistants",
        element: (
          <SuspenseWrap>
            <AssistantsPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/assistants/:id",
        element: (
          <SuspenseWrap>
            <AssistantDetailPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/billing",
        element: (
          <SuspenseWrap>
            <BillingPage />
          </SuspenseWrap>
        ),
      },
      {
        path: "/support",
        element: (
          <SuspenseWrap>
            <SupportPage />
          </SuspenseWrap>
        ),
      },
    ],
  },
]);

// ─── App ─────────────────────────────────────────────────────────────

export function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <RouterProvider router={router} />
        {isMockMode() && <div className="mock-indicator">Demo Mode</div>}
      </ErrorBoundary>
    </AuthProvider>
  );
}
